import { Column, Row } from "@silevis/reactgrid";
import { useEffect, useState } from "react";
import { useData } from "../Contexts/DataProvider";
import BaseTable from "./BaseTable";
import {
  columnWidth,
  isDigitsOnly,
  originalFtePriorityColumns,
  renameHeaderRow,
} from "./TableHelpers";

const rowHeight = 35;

const sortColumns = (columns: Column[]): Column[] => {
  const isPriority = (col: Column) =>
    originalFtePriorityColumns.includes(String(col.columnId));
  return [
    ...columns.filter(isPriority),
    ...columns
      .filter((c) => !isPriority(c))
      .sort((a, b) => {
        const aId = String(a.columnId);
        const bId = String(b.columnId);

        if (isDigitsOnly(aId) && isDigitsOnly(bId)) {
          return Number(aId) - Number(bId);
        }
        return aId.localeCompare(bId);
      }),
  ];
};

const getColumns = (headCount: any[]) => {
  if (!headCount || !headCount.length) return [];
  const uniqueColumns = new Set<string>();
  headCount.forEach((item: any) => {
    Object.keys(item).forEach((key) => {
      if (
        key !== "new_originalfteid" &&
        key !== "new_submitted" &&
        key !== "@odata.etag"
      )
        uniqueColumns.add(key);
    });
  });
  let columnsArray: Column[] = [];
  originalFtePriorityColumns.forEach((columnId) => {
    columnsArray.push({
      columnId,
      width: columnWidth(columnId),
      resizable: true,
    });
    uniqueColumns.delete(columnId);
  });
  uniqueColumns.forEach((key) =>
    columnsArray.push({
      columnId: key,
      width: 90,
      resizable: true,
    })
  );
  return sortColumns(columnsArray);
};

const calculateSummaries = (rows: any, columns: Column[]) => {
  const summaries: Record<string, number> = {};
  rows.forEach((row: any) => {
    columns.forEach((col) => {
      const val = row[col.columnId];
      if (typeof val === "number" && !isNaN(val)) {
        summaries[col.columnId] = (summaries[col.columnId] || 0) + val;
      }
    });
  });
  return summaries;
};

const getSummaryRow = (summaries: any, columns: Column[]) => {
  const cells = columns.map((col) => ({
    type: "text" as const,
    text:
      col.columnId === "new_codecompany"
        ? "Summary"
        : summaries[col.columnId]?.toLocaleString() ?? "",
    groupId: col.columnId,
    readOnly: true,
  }));
  return { rowId: "summary", height: rowHeight, cells };
};

const buildRows = (
  headCount: any[],
  columns: Column[],
  submitted: boolean
): Row[] => {
  const headerRow: any = renameHeaderRow(columns);
  const dataRows = headCount.map((row, idx) => ({
    rowId: idx,
    height: rowHeight,
    cells: columns.map((col) => {
      const value = row[col.columnId];
      const readOnly = true;
      if (typeof value === "number") {
        return {
          type: "number",
          value,
          groupId: col.columnId,
          readOnly,
        };
      } else if (typeof value === "string") {
        return {
          type: "text",
          text: value,
          groupId: col.columnId,
          readOnly,
        };
      } else {
        return {
          type: "text",
          text: "",
          groupId: col.columnId,
          readOnly,
        };
      }
    }),
    id: row.headcountid,
  }));

  const summaries = calculateSummaries(headCount, columns);
  const summaryRow = getSummaryRow(summaries, columns);

  return [headerRow, ...dataRows, summaryRow];
};

const OriginalFteTable = ({ submitted = false }) => {
  const { data } = useData();
  const headCount = data?.value ?? [];

  const [columns, setColumns] = useState<Column[]>(() => getColumns(headCount));
  const [rows, setRows] = useState<Row[]>(
    buildRows(headCount, columns, submitted)
  );

  useEffect(() => {
    const newCols = getColumns(headCount);
    setColumns((prev) => {
      const prevWidths = Object.fromEntries(
        prev.map((col) => [col.columnId, col.width])
      );
      return newCols.map((col) =>
        prevWidths[col.columnId]
          ? { ...col, width: prevWidths[col.columnId] }
          : col
      );
    });
  }, [headCount.length]);

  return (
    <BaseTable
      columns={columns}
      setColumns={setColumns}
      rows={rows}
      stickyLeftColumns={2}
      onCellsChanged={() => {}}
      modifyCellValues={() => {}}
    />
  );
};

export default OriginalFteTable;
